/* ============================================
   MATCHING-RUNNER
   ============================================
   Lädt alle Pflegekräfte und alle Arbeitgeber-
   Einrichtungen, berechnet für jedes Paar den
   Matchscore über die Engine und speichert das
   Ergebnis (inkl. vollständiger Berechnung) in
   die Tabelle "matches" — mit arbeitgeber_id als
   Match-Ziel.
   ============================================ */

import { supabase } from '../supabase'
import { computeMatch } from './engine'
import type { MatchCandidate, MatchJob, Verfuegbarkeit, Haeufigkeit } from './types'

interface PkRow {
  id: string
  vorname: string
  nachname: string
  qualifikation: string | null
  anerkennungsstatus: string | null
  berufserfahrung_jahre: number | null
  bundesland: string | null
  plz: string | null
  umzug_moeglich: boolean | null
  max_arbeitsweg_km: number | null
  gewuenschte_bereiche: string[] | null
  gewuenschter_bereich: string | null
  gewuenschte_arbeitsmodelle: string[] | null
  gewuenschtes_arbeitsmodell: string | null
  gewuenschte_wochenstunden: number | null
  mindestgehalt_brutto: number | null
  gehaltswunsch_brutto: number | null
  fruehester_wechselzeitpunkt: string | null
  nachtdienst_moeglich: string | null
  wochenenddienst_moeglich: string | null
  geteilte_dienste_ok: string | null
  rufbereitschaft_ok: string | null
  no_gos: string[] | null
  slider_antworten: Record<string, number> | null
}

interface WgRow {
  pflegekraft_id: string
  grund: string
  intensitaet: number | null
  top_rang: number | null
}

interface AgRow {
  id: string
  name: string
  wunschqualifikationen: string[] | null
  einsatzbereiche: string[] | null
  arbeitsmodelle: string[] | null
  wochenstunden_min: number | null
  wochenstunden_max: number | null
  mindestberufserfahrung: number | null
  anerkennung_erforderlich: string | null
  bundesland: string | null
  plz: string | null
  gehaltsrahmen_min: number | null
  gehaltsrahmen_max: number | null
  nachtdienst_erforderlich: string | null
  wochenenddienst_erforderlich: string | null
  geteilte_dienste: string | null
  rufbereitschaft: string | null
  slider_antworten: Record<string, number> | null
  fakten: Record<string, unknown> | null
}

/**
 * Leitet AG-Slider-Werte aus den konkreten Faktenfeldern ab.
 * Damit der Arbeitgeber-Funnel diese Slider nicht doppelt fragen muss
 * (z. B. „Wie planbar sind Dienstpläne?" + „Dienstplan-Vorlauf in Wochen?").
 */
function deriveSliderValues(ag: AgRow): Record<string, number> {
  const f = (ag.fakten || {}) as Record<string, unknown>
  const out: Record<string, number> = {}

  // Dienstplan-Vorlauf → schedule_notice
  if (typeof f.dienstplan_vorlauf_wochen === 'number') {
    const w = f.dienstplan_vorlauf_wochen
    out.schedule_notice = w >= 6 ? 5 : w >= 4 ? 3 : w >= 2 ? 0 : -3
  }
  // Einspringen → no_free_time_callin
  switch (f.einspringen_haeufigkeit) {
    case 'nie': out.no_free_time_callin = 5; break
    case 'selten': out.no_free_time_callin = 3; break
    case 'gelegentlich': out.no_free_time_callin = -1; break
    case 'haeufig': out.no_free_time_callin = -4; break
  }
  // Überstunden → overtime_control + workload_level
  if (typeof f.ueberstunden_pro_monat === 'number') {
    const u = f.ueberstunden_pro_monat
    if (u <= 3) { out.overtime_control = 5; out.workload_level = 4 }
    else if (u <= 8) { out.overtime_control = 2; out.workload_level = 1 }
    else if (u <= 15) { out.overtime_control = -1; out.workload_level = -2 }
    else { out.overtime_control = -4; out.workload_level = -4 }
  }
  // Wochenend-Frequenz → weekend_predictability
  switch (f.wochenend_frequenz) {
    case 'keine': out.weekend_predictability = 5; break
    case 'jedes_4': out.weekend_predictability = 3; break
    case 'individuell': out.weekend_predictability = 2; break
    case 'jedes_3': out.weekend_predictability = 0; break
    case 'jedes_2': out.weekend_predictability = -3; break
  }
  // Hebehilfen → physical_relief
  if (f.hebehilfen_vorhanden === true) out.physical_relief = 3
  else if (f.hebehilfen_vorhanden === false) out.physical_relief = -2
  // Digitale Doku → documentation_relief
  if (f.digitale_doku === true) out.documentation_relief = 3
  else if (f.digitale_doku === false) out.documentation_relief = -2
  // Hilfskräfte → support_staff
  if (f.hilfskraefte_vorhanden === true) out.support_staff = 3
  else if (f.hilfskraefte_vorhanden === false) out.support_staff = -2
  // Feedback-Rhythmus → feedback_culture
  switch (f.feedback_rhythmus) {
    case 'monatlich': out.feedback_culture = 5; break
    case 'quartalsweise': out.feedback_culture = 3; break
    case 'jaehrlich': out.feedback_culture = -1; break
    case 'selten': out.feedback_culture = -4; break
  }
  // schedule_reliability als Mittel aus einspringen + überstunden
  const relComponents = [out.no_free_time_callin, out.overtime_control].filter((v): v is number => v !== undefined)
  if (relComponents.length > 0) {
    out.schedule_reliability = Math.round(relComponents.reduce((a, b) => a + b, 0) / relComponents.length)
  }
  // family_compatibility als Mittel aus wochenende + einspringen + überstunden
  const famComponents = [out.weekend_predictability, out.no_free_time_callin, out.overtime_control].filter((v): v is number => v !== undefined)
  if (famComponents.length > 0) {
    out.family_compatibility = Math.round(famComponents.reduce((a, b) => a + b, 0) / famComponents.length)
  }
  return out
}

const v = (x: string | null): Verfuegbarkeit | null => (x as Verfuegbarkeit | null) ?? null
const h = (x: string | null): Haeufigkeit | null => (x as Haeufigkeit | null) ?? null

function toCandidate(pk: PkRow, wg: WgRow[]): MatchCandidate {
  return {
    id: pk.id,
    vorname: pk.vorname,
    nachname: pk.nachname,
    qualifikation: pk.qualifikation,
    anerkennungsstatus: pk.anerkennungsstatus,
    berufserfahrung_jahre: pk.berufserfahrung_jahre,
    bundesland: pk.bundesland,
    plz: pk.plz,
    umzug_moeglich: pk.umzug_moeglich,
    max_arbeitsweg_km: pk.max_arbeitsweg_km,
    gewuenschte_bereiche:
      pk.gewuenschte_bereiche ?? (pk.gewuenschter_bereich ? [pk.gewuenschter_bereich] : null),
    gewuenschte_arbeitsmodelle:
      pk.gewuenschte_arbeitsmodelle ?? (pk.gewuenschtes_arbeitsmodell ? [pk.gewuenschtes_arbeitsmodell] : null),
    gewuenschte_wochenstunden: pk.gewuenschte_wochenstunden,
    mindestgehalt_brutto: pk.mindestgehalt_brutto,
    gehaltswunsch_brutto: pk.gehaltswunsch_brutto,
    fruehester_wechselzeitpunkt: pk.fruehester_wechselzeitpunkt,
    // Frühdienst/Spätdienst werden nicht mehr abgefragt — als 'ja' angenommen
    fruehdienst_moeglich: 'ja',
    spaetdienst_moeglich: 'ja',
    nachtdienst_moeglich: v(pk.nachtdienst_moeglich),
    wochenenddienst_moeglich: v(pk.wochenenddienst_moeglich),
    geteilte_dienste_ok: v(pk.geteilte_dienste_ok),
    rufbereitschaft_ok: v(pk.rufbereitschaft_ok),
    no_gos: pk.no_gos,
    slider_antworten: pk.slider_antworten,
    wechselgruende: wg.map((w) => ({
      grund: w.grund,
      intensitaet: w.intensitaet ?? 0,
      top_rang: w.top_rang,
    })),
  }
}

function toJob(ag: AgRow): MatchJob {
  const derived = deriveSliderValues(ag)
  // AG-gerated Slider überschreiben abgeleitete Werte (sollte sich nicht überschneiden,
  // da derived-Slider im Funnel nicht abgefragt werden).
  const slider_antworten = { ...derived, ...(ag.slider_antworten || {}) }
  return {
    id: ag.id,
    titel: ag.name,
    arbeitgeber_name: ag.name,
    erforderliche_qualifikationen: ag.wunschqualifikationen,
    anerkennung_erforderlich: v(ag.anerkennung_erforderlich),
    mindestberufserfahrung: ag.mindestberufserfahrung,
    bundesland: ag.bundesland,
    plz: ag.plz,
    einsatzbereiche: ag.einsatzbereiche,
    arbeitsmodelle: ag.arbeitsmodelle,
    wochenstunden_min: ag.wochenstunden_min,
    wochenstunden_max: ag.wochenstunden_max,
    gehalt_min: ag.gehaltsrahmen_min,
    gehalt_max: ag.gehaltsrahmen_max,
    // Einrichtungsprofil hat kein konkretes Startdatum
    startdatum: null,
    flexibler_start: true,
    nachtdienst_erforderlich: v(ag.nachtdienst_erforderlich),
    wochenenddienst_erforderlich: h(ag.wochenenddienst_erforderlich),
    geteilte_dienste: h(ag.geteilte_dienste),
    rufbereitschaft: h(ag.rufbereitschaft),
    slider_antworten,
  }
}

export interface MatchingRunResult {
  pflegekraefte: number
  einrichtungen: number
  matches: number
}

export async function runMatchingAll(): Promise<MatchingRunResult> {
  const [pkRes, wgRes, agRes] = await Promise.all([
    supabase.from('pflegekraefte').select('*'),
    supabase.from('pflegekraft_wechselgruende').select('pflegekraft_id, grund, intensitaet, top_rang'),
    supabase.from('arbeitgeber').select('*').neq('status', 'abgesagt'),
  ])

  if (pkRes.error) throw pkRes.error
  if (wgRes.error) throw wgRes.error
  if (agRes.error) throw agRes.error

  const pks = (pkRes.data || []) as PkRow[]
  const wgs = (wgRes.data || []) as WgRow[]
  const ags = (agRes.data || []) as AgRow[]

  if (pks.length === 0 || ags.length === 0) {
    return { pflegekraefte: pks.length, einrichtungen: ags.length, matches: 0 }
  }

  const wgByPk = new Map<string, WgRow[]>()
  for (const w of wgs) {
    const list = wgByPk.get(w.pflegekraft_id) || []
    list.push(w)
    wgByPk.set(w.pflegekraft_id, list)
  }

  const now = new Date().toISOString()
  const rows: Record<string, unknown>[] = []

  for (const pk of pks) {
    const candidate = toCandidate(pk, wgByPk.get(pk.id) || [])
    for (const ag of ags) {
      const result = computeMatch(candidate, toJob(ag))
      rows.push({
        pflegekraft_id: pk.id,
        arbeitgeber_id: ag.id,
        stelle_id: null,
        score: result.totalScore,
        kategorie: result.kategorie,
        muss_kriterien_erfuellt: !result.knockout,
        score_hard_facts: result.hardFactsScore,
        score_arbeitsplatz: result.arbeitsplatzScore,
        score_retention: result.retentionScore,
        risiko_abzug: result.riskPenalty,
        knockout: result.knockout,
        berechnung: result,
        berechnet_am: now,
      })
    }
  }

  const CHUNK = 250
  for (let i = 0; i < rows.length; i += CHUNK) {
    const chunk = rows.slice(i, i + CHUNK)
    const { error } = await supabase
      .from('matches')
      .upsert(chunk, { onConflict: 'pflegekraft_id,arbeitgeber_id' })
    if (error) throw error
  }

  return { pflegekraefte: pks.length, einrichtungen: ags.length, matches: rows.length }
}
